import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TablePagination,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import { WidgetOrigin } from '../../../types/Statistics.types';
import { Order } from '../Statistics.types';
import { exportCSV } from '../utils/csvExport';
import { generateCSVData } from '../utils/dataTransformers';
import { WidgetOriginsTableDesktop } from './WidgetOriginsTableDesktop';
import { WidgetOriginsTableMobile } from './WidgetOriginsTableMobile';

export type WidgetOriginKey = keyof WidgetOrigin;

interface WidgetOriginsTableProps {
  rows: WidgetOrigin[];
  isError?: boolean;
}

export const WidgetOriginsTable: React.FC<WidgetOriginsTableProps> = ({
  rows,
  isError = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { account, accountList } = useAuthContext();
  const showAccount = account === 'admin';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<WidgetOriginKey>('count');

  const accountName = useCallback(
    (id?: string) => (id ? accountList[id] || id : ''),
    [accountList]
  );

  const sortedRows = useMemo(() => {
    const multiplier = order === 'asc' ? 1 : -1;
    const value = (row: WidgetOrigin) =>
      orderBy === 'count'
        ? row.count
        : (orderBy === 'account'
            ? accountName(row.account)
            : row[orderBy] || ''
          ).toLowerCase();
    return [...rows].sort((a, b) => {
      const aValue = value(a);
      const bValue = value(b);
      if (aValue > bValue) return multiplier;
      if (aValue < bValue) return -multiplier;
      return 0;
    });
  }, [rows, order, orderBy, accountName]);

  const handleSort = (property: WidgetOriginKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // New rows (another date range) start on the first page.
  useEffect(() => {
    setPage(0);
  }, [rows]);

  // Until that reset has rendered, never slice past the last page of the new rows.
  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const rowOffset = currentPage * rowsPerPage;
  const paginatedRows = sortedRows.slice(rowOffset, rowOffset + rowsPerPage);

  const handleDownload = () => {
    exportCSV(
      generateCSVData('widgetOrigins', { rows: sortedRows, showAccount }, t),
      t('statistics.widgetOrigins.title', 'Widget searches')
    );
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="body2" sx={{ fontStyle: 'italic', flex: 1 }}>
          {t('statistics.help.widgetOrigins')}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label={String(
            t('statistics.download.ariaLabel', 'Download options')
          )}
        >
          <Tooltip title={t('statistics.csv', 'Download list as TSV')}>
            <ToggleButton
              value="download"
              onClick={handleDownload}
              aria-label={String(t('statistics.csv', 'Download list as TSV'))}
              size="small"
              disabled={isError || rows.length === 0}
              data-testid="widget-origins-download"
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>

      {isError ? (
        <Typography variant="body1" sx={{ py: 24 }} role="alert">
          {t(
            'statistics.widgetOrigins.error',
            'Widget searches could not be loaded. Please try again.'
          )}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body1" sx={{ py: 24 }} role="status">
          {t(
            'statistics.widgetOrigins.empty',
            'No widget searches in the selected period.'
          )}
        </Typography>
      ) : isMobile ? (
        <WidgetOriginsTableMobile
          rows={paginatedRows}
          showAccount={showAccount}
          accountName={accountName}
        />
      ) : (
        <WidgetOriginsTableDesktop
          rows={paginatedRows}
          rowOffset={rowOffset}
          total={rows.length}
          showAccount={showAccount}
          accountName={accountName}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
        />
      )}

      {/* The rows of a failed refetch are stale, so no pagination under the error. */}
      {!isError && (
        <>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: `1px solid var(--color-dark-greyish-blue)`,
            }}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={t(
              'statistics.table.rowsPerPage',
              'Rows per page:'
            )}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('statistics.table.of', 'of')} ${
                count !== -1 ? count : `more than ${to}`
              }`
            }
            SelectProps={{
              inputProps: {
                'aria-label': String(
                  t('statistics.table.rowsPerPage', 'Rows per page')
                ),
                'aria-describedby': 'widget-origins-rows-per-page-helper',
                id: 'widget-origins-rows-per-page-select',
              },
              native: false,
            }}
          />
          <span id="widget-origins-rows-per-page-helper" className="sr-only">
            {t(
              'statistics.table.rowsPerPageHelp',
              'Select the number of rows to display per page'
            )}
          </span>
        </>
      )}
    </Box>
  );
};
